import { ChangeEvent, FC } from 'react';

// Third party
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Icon from '@mdi/react';
import { mdiAsterisk, mdiAt } from '@mdi/js';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormHelperText from '@mui/material/FormHelperText';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';

// Helpers
import { BaseLabel } from '@mgm/common/components/base';
// Hooks
import { BASE_COLORS } from '@mgm/common/utils';
import { useFormRules } from '../../hooks/useForm';

// Types
import {
  TBaseField,
} from './type';

// Components

const EmailField: FC<TBaseField> = ({
  defaultValue,
  readOnly = false,
  isRequired = false,
  onChange,
  disabled = false,
  error,
  helperText,
}) => {
  const { emailValidation } = useFormRules();
  const { t } = useTranslation('inputs');

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    field: { ref, ...inputProps },
  } = useController({
    name: 'email',
    control,
    rules: {
      required: isRequired,
      validate: {
        emailValidation,
      },
    },
    defaultValue: defaultValue || '',
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    inputProps.onChange(e);

    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <Box>
      <div className="align-center">
        <BaseLabel letterSpacing="-0.5px">{t('email', { ns: 'inputs' })}</BaseLabel>
        {isRequired && (
          <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
        )}
      </div>
      <OutlinedInput
        {...inputProps}
        readOnly={readOnly}
        disabled={disabled}
        onChange={handleChange}
        size="small"
        inputRef={ref}
        id="email"
        placeholder={t('enterEmail')}
        fullWidth
        autoComplete="email"
        autoFocus
        error={!!error || !!errors.email}
        endAdornment={(
          <InputAdornment position="end">
            <IconButton
              disableRipple
              color="primary"
              aria-label="email icon"
              edge="end"
            >
              <Icon
                color={error || errors.email ? BASE_COLORS.error : BASE_COLORS.primary}
                path={mdiAt}
                size={0.7}
              />
            </IconButton>
          </InputAdornment>
        )}
      />
      {(error || !!errors.email) && (
        <FormHelperText error>{(helperText || errors.email?.message || '') as any}</FormHelperText>
      )}
    </Box>
  );
};

export {
  EmailField,
};
